import * as React from 'react';
import type { ITextField } from '@fluentui/react/lib/TextField';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { useTextChangeCallback, useProjectId } from 'owa-timestream-asset-sdk';
import { updateSubtodo, deleteSubtodo } from 'owa-timestream-asset-sdk-tasks';
import type { SubTaskViewProps } from '../schema';
import { toggleSubTaskCompletion } from '../actions';
import { useOnBlurUpdate } from 'owa-react-hooks/lib/useOnBlurUpdate';
import handleSubtaskShift from '../utils/handleSubtaskShift';

export default function useSubTaskViewState({
    parentTaskId,
    subtaskId,
    subject,
    isComplete,
}: SubTaskViewProps) {
    const projectId = useProjectId();
    const [subtaskSubject, setSubtaskSubject] = React.useState<string>(subject);
    const subtaskSubjectFieldRef = React.useRef<ITextField>(null);

    const onTextFieldChange = useTextChangeCallback(setSubtaskSubject);
    const commitChange = React.useCallback(() => {
        if (!!subtaskSubject && subtaskSubject !== subject) {
            updateSubtodo({ Subject: subtaskSubject }, parentTaskId, subtaskId);
        } else if (!subtaskSubject) {
            deleteSubtodo(parentTaskId, subtaskId);
        }
    }, [parentTaskId, subtaskId, subtaskSubject, subject]);

    const onTextFieldBlur = useOnBlurUpdate(commitChange);

    const onKeyDown = React.useCallback(
        (ev: React.KeyboardEvent) => {
            switch (ev.keyCode) {
                case KeyCodes.enter:
                    subtaskSubjectFieldRef?.current?.blur();
                    break;
                case KeyCodes.escape:
                    setSubtaskSubject(subject!);
                    break;
            }
        },
        [subtaskSubjectFieldRef, subject, parentTaskId, subtaskId]
    );

    const onSubtaskKeyDown = React.useCallback(
        (ev: React.KeyboardEvent) => {
            const isShiftKeyPressed = ev.shiftKey;
            const isAltKeyPressed = ev.altKey || ev.metaKey;
            const canSubtaskBeShifted = isShiftKeyPressed && isAltKeyPressed;
            switch (ev.keyCode) {
                case KeyCodes.del:
                    ev.preventDefault();
                    ev.stopPropagation();
                    deleteSubtodo(parentTaskId, subtaskId);
                    break;
                case KeyCodes.up:
                case KeyCodes.down:
                    canSubtaskBeShifted &&
                        handleSubtaskShift(
                            projectId,
                            parentTaskId,
                            subtaskId,
                            ev.keyCode === KeyCodes.down /**isShiftedDown */
                        );
                    break;
            }
        },
        [parentTaskId, subtaskId]
    );

    const toggleComplete = React.useCallback(() => {
        if (subject) {
            toggleSubTaskCompletion(parentTaskId, subtaskId, !isComplete);
            updateSubtodo({ IsCompleted: !isComplete }, parentTaskId, subtaskId);
        }
    }, [parentTaskId, subtaskId, subject, isComplete]);

    return {
        onSubtaskKeyDown,
        textFieldProps: {
            componentRef: subtaskSubjectFieldRef,
            onBlur: onTextFieldBlur,
            onChange: onTextFieldChange,
            onKeyDown: onKeyDown,
            value: subtaskSubject,
        },
        isComplete: isComplete,
        onToggleComplete: toggleComplete,
    };
}
